/**
 * Japanese (ja-JP) Translations
 * 日文翻译
 */

import type { PricingMessages } from './types';

const jaJP: PricingMessages = {
    pageTitle: '料金プラン',

    hero: {
        title: 'あなたに最適なプランを選択',
        subtitle: '柔軟な価格設定、強力な機能',
        billingToggle: {
            monthly: '月払い',
            annual: '年払い',
            saveLabel: '{percent}% お得',
        },
    },

    plans: {
        lite: {
            name: 'ライト',
            description: '毎月すべての機能を1回お試しいただけます',
            cta: '今すぐ購入',
            priceLabel: '{amount} / 月',
        },
        pro: {
            name: 'プロ',
            description: '専門家向けの無制限の下書き作成',
            cta: '今すぐ購入',
            priceLabel: '{amount} / 月',
            features: {
                unlimited_drafts: 'プロのドキュメント枠',
                priority_support: '優先サポート',
            },
        },
        max: {
            name: 'Max',
            description: '大量の特許作成向けの無制限ドキュメント作成',
            cta: '今すぐ購入',
            priceLabel: '{amount} / 月',
            features: {
                unlimited_documents: 'ドキュメント作成数無制限',
            },
        },
        enterprise: {
            name: 'エンタープライズ',
            description: 'チームと組織向けのカスタムソリューション',
            cta: 'お問い合わせ',
            priceLabel: 'カスタム価格',
        },
    },

    features: {
        drafting: {
            title: '特許作成',
            items: {
                specification_generation: '明細書生成',
                claims_generation: '請求項生成',
                abstract_generation: '要約生成',
                embodiment_expansion: '実施形態の拡張',
                figure_handling: '図面処理',
                drafts_per_month: '月間作成数',
            },
        },
        export: {
            title: 'エクスポートとフォーマット',
            items: {
                export_word: 'Wordエクスポート',
                export_pdf: 'PDFエクスポート',
                multi_jurisdiction: '複数の管轄区域のフォーマット',
                custom_templates: 'カスタムテンプレート',
            },
        },
        collaboration: {
            title: 'コラボレーション',
            items: {
                workspace_share: '共有',
                workspace_comment: 'コメント',
                workspace_edit: '共同編集',
                version_history: 'バージョン履歴',
            },
        },
        administration: {
            title: '管理',
            items: {
                admin_backend: '管理バックエンド',
                user_management: 'ユーザー管理',
                usage_analytics: '使用状況分析',
            },
        },
        security: {
            title: 'セキュリティ',
            items: {
                data_encryption: 'データ暗号化',
                sso: 'シングルサインオン(SSO)',
                audit_logs: '監査ログ',
            },
        },
        support: {
            title: 'サポート',
            items: {
                support_model: 'サポートモデル',
                training: 'トレーニングとオンボーディング',
            },
        },
        values: {
            unlimited: '無制限',
            included: '含まれています',
            notIncluded: '含まれていません',
            custom: 'カスタム',
            allJurisdictions: 'すべての特許庁',
        },
    },

    faq: {
        title: 'よくある質問',
        items: [
            {
                question: '生成された特許の品質はどうですか？',
                answer:
          '当社のAIシステムは、大量の高品質な特許データでトレーニングされており、プロフェッショナルグレードの特許文書を生成できます。すべてのコンテンツは、明確な論理と正確な表現を保証するように慎重に設計されています。',
            },
            {
                question: 'どの特許庁のフォーマットがサポートされていますか？',
                answer:
          '中国国家知識産権局(CNIPA)、米国特許商標庁(USPTO)、欧州特許庁(EPO)、日本国特許庁(JPO)を含む主要な特許庁のフォーマット、およびその他多くの管轄区域をサポートしています。',
            },
            {
                question: 'データセキュリティはどのように保証されていますか？',
                answer:
          '銀行レベルの暗号化技術を使用してデータを保護しています。すべてのデータ転送はSSLで暗号化されています。データ保護規制を厳守し、お客様の特許内容を第三者と共有することはありません。',
            },
            {
                question: '技術的な背景が必要ですか？',
                answer:
          'いいえ。当社のシステムはシンプルで直感的に設計されており、誰でも簡単に使い始めることができます。専門ユーザー向けには、高度な機能とカスタマイズオプションも提供しています。',
            },
            {
                question: 'カスタマイズは可能ですか？',
                answer:
          'はい。エンタープライズプランでは、専用テンプレート、ワークフローのカスタマイズ、API統合などを含む完全なカスタマイズサービスを提供しています。お客様の具体的なニーズに基づいてソリューションを提供します。',
            },
            {
                question: '料金はどのように計算されますか？',
                answer:
          'ライトプランは月額9.9ドルで、月1件の下書きが可能です。プロプランは月払いと年払いの両方のオプションがあり、年払いでは20%の割引が適用されます。エンタープライズ価格はチームのサイズと要件に基づいてカスタマイズされます。',
            },
            {
                question: 'チームコラボレーションをサポートしていますか？',
                answer:
          'プロプランとエンタープライズプランの両方が、文書の共有、コメント、バージョン履歴などのチームコラボレーション機能をサポートしています。エンタープライズプランでは、高度な共同編集とユーザー管理機能も提供しています。',
            },
            {
                question: '満足できない場合は返金できますか？',
                answer:
          '7日間の返金保証を提供しています。サービスに満足できない場合は、購入後7日以内に全額返金を申請できます。',
            },
        ],
    },

    cta: {
        title: '始める準備はできましたか？',
        subtitle: 'あなたに合ったプランを選んで、今日から特許作成の旅を始めましょう',
        buttonText: '始める',
        selectPlan: 'プランを選択',
        contactSales: '営業に問い合わせ',
        learnMore: '詳細を見る',
        getStarted: '始める',
        emailSubject: 'Eureka Drafting - エンタープライズプランについて',
        planSelected: '{plan}プランが選択されました',
        getStartedMessage: '登録ページにリダイレクトしています...',
    },

    errors: {
        loadPlansFailed: '料金情報の読み込みに失敗しました。後でもう一度お試しください',
        invalidPlan: '無効な料金プラン',
        networkError: 'ネットワーク接続に失敗しました',
    },

    common: {
        perMonth: '/ 月',
        perYear: '/ 年',
        billedMonthly: '月額請求',
        billedAnnually: '年額請求',
        popular: '人気',
        recommended: 'おすすめ',
        comingSoon: '近日公開',
    },

    payment: {
        verifying: '支払いを確認中...',
        pleaseWait: 'お支払いの確認をお待ちください',
        backToPricing: '料金ページに戻る',
        tryAgain: '再試行',

        success: {
            title: '支払い成功！',
            message: 'ご購読ありがとうございます。アカウントが有効化されました。',
        },

        failed: {
            title: '支払い失敗',
            message: 'お支払いを処理できませんでした。もう一度お試しください。',
        },

        canceled: {
            title: '支払いがキャンセルされました',
            message: '支払いをキャンセルしました。料金は発生していません。',
        },

        errors: {
            createSessionFailed: '支払いセッションの作成に失敗しました。もう一度お試しください。',
            verifyFailed: '支払い状態の確認に失敗しました',
            verificationFailed: '支払いの確認に失敗しました',
            verificationError: '確認中にエラーが発生しました',
            invalidCallback: '無効な支払いコールバック',
            noPriceId: '価格情報が設定されていません',
        },
    },
};

export default jaJP;
